/* eslint-disable jsx-a11y/media-has-caption */
import React from 'react';
import PropTypes from 'prop-types';
import {
    Alert,
    Box,
    Button,
    Chip,
    Divider,
    MenuItem,
    Stack,
    TextField,
    Typography,
    alpha,
    useTheme,
} from '@mui/material';
import { Iconify } from 'src/components';
import {
    getSpeakerLabel,
    getSpeakerRoleValue,
    statusConfig,
} from './consultationRecordingsUtils';

const MAX_VISIBLE_UTTERANCES = 12;

const roleOptions = [
    { value: 'practitioner', label: 'Practitioner', icon: 'solar:user-id-bold' },
    { value: 'patient', label: 'Patient', icon: 'solar:user-rounded-bold' },
    { value: 'unknown', label: 'Unknown', icon: 'solar:question-circle-bold' },
];

const statusColor = (theme, color) => theme.palette[color]?.main || theme.palette.grey[500];

const getDisplaySpeakerLabel = (speaker, speakerMap = {}) => {
    if (getSpeakerRoleValue(speaker, speakerMap) === 'unknown') {
        return getSpeakerLabel(speaker);
    }
    return getSpeakerLabel(speaker, speakerMap);
};

function EmptyRecordingDetail() {
    const theme = useTheme();
    return (
        <Box
            sx={{
                minHeight: 320,
                p: 4,
                borderRadius: 1,
                border: `1px dashed ${alpha(theme.palette.grey[500], 0.24)}`,
                bgcolor: alpha(theme.palette.grey[500], 0.03),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
            }}
        >
            <Box sx={{ maxWidth: 320 }}>
                <Iconify icon="solar:document-medicine-bold" width={36} height={36} sx={{ color: 'text.disabled' }} />
                <Typography variant="subtitle1" sx={{ mt: 1, fontWeight: 700 }}>
                    Select a recording
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
                    Recordings, transcripts and generated notes appear here.
                </Typography>
            </Box>
        </Box>
    );
}

function RecordingHeader({ recording, cfg }) {
    const theme = useTheme();
    return (
        <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', sm: 'center' }}
        >
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        flexShrink: 0,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: alpha(statusColor(theme, cfg.color), 0.1),
                    }}
                >
                    <Iconify icon={cfg.icon} width={20} height={20} sx={{ color: statusColor(theme, cfg.color) }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle1" noWrap sx={{ fontWeight: 700 }}>
                        Consultation recording
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {recording?.created_at_human || recording?.started_at || 'Just now'}
                    </Typography>
                </Box>
            </Stack>
            <Chip
                size="small"
                color={cfg.color}
                icon={<Iconify icon={cfg.icon} width={14} height={14} />}
                label={cfg.label}
                sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
            />
        </Stack>
    );
}

RecordingHeader.propTypes = {
    recording: PropTypes.object.isRequired,
    cfg: PropTypes.object.isRequired,
};

function SpeakerAssignmentPanel({
    canManage,
    recording,
    speakerIds,
    hasUnknownSpeakers,
    onSpeakerMapChange,
}) {
    if (speakerIds.length === 0) return null;

    return (
        <Box sx={{ mt: 2.5 }}>
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                sx={{ mb: 1 }}
            >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2">Speakers</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Confirm roles before saving the note.
                    </Typography>
                </Box>
                {hasUnknownSpeakers && (
                    <Chip
                        size="small"
                        color="warning"
                        icon={<Iconify icon="solar:danger-triangle-bold" width={14} height={14} />}
                        label="Needs review"
                        sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
                    />
                )}
            </Stack>

            {hasUnknownSpeakers && (
                <Alert severity="warning" sx={{ mb: 1.5 }}>
                    Assign each speaker as practitioner or patient. The generated note refreshes after a change.
                </Alert>
            )}

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                    gap: 1,
                }}
            >
                {speakerIds.map((speakerId) => {
                    const value = getSpeakerRoleValue(speakerId, recording?.speaker_map);
                    const selectedRole = roleOptions.find((option) => option.value === value) || roleOptions[2];
                    return (
                        <TextField
                            key={speakerId}
                            select
                            size="small"
                            label={getSpeakerLabel(speakerId)}
                            disabled={!canManage || Boolean(recording?.lead_note)}
                            value={value}
                            onChange={(event) => onSpeakerMapChange(recording.id, {
                                ...(recording?.speaker_map || {}),
                                [speakerId]: event.target.value,
                            })}
                            InputProps={{
                                startAdornment: (
                                    <Iconify
                                        icon={selectedRole.icon}
                                        width={16}
                                        height={16}
                                        sx={{ mr: 1, color: value === 'unknown' ? 'warning.main' : 'text.secondary' }}
                                    />
                                ),
                            }}
                        >
                            {roleOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    );
                })}
            </Box>
        </Box>
    );
}

SpeakerAssignmentPanel.propTypes = {
    canManage: PropTypes.bool.isRequired,
    recording: PropTypes.object.isRequired,
    speakerIds: PropTypes.array.isRequired,
    hasUnknownSpeakers: PropTypes.bool.isRequired,
    onSpeakerMapChange: PropTypes.func.isRequired,
};

function TranscriptPanel({ recording, utterances }) {
    const theme = useTheme();
    const [showFullTranscript, setShowFullTranscript] = React.useState(false);
    const hasLongTranscript = utterances.length > MAX_VISIBLE_UTTERANCES;
    const visibleUtterances = showFullTranscript || !hasLongTranscript
        ? utterances
        : utterances.slice(0, MAX_VISIBLE_UTTERANCES);

    React.useEffect(() => {
        setShowFullTranscript(false);
    }, [recording?.id]);

    if (utterances.length === 0) {
        if (!['uploaded', 'processing'].includes(recording?.status)) return null;
        return (
            <Alert severity="info" sx={{ mt: 2 }}>
                Transcript and note draft are being prepared.
            </Alert>
        );
    }

    return (
        <Box sx={{ mt: 2.5 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle2" sx={{ flex: 1 }}>
                    Transcript
                </Typography>
                <Chip size="small" label={`${utterances.length} turns`} />
            </Stack>

            <Stack spacing={1} sx={{ maxHeight: showFullTranscript ? 420 : 340, overflow: 'auto', pr: 0.5 }}>
                {visibleUtterances.map((item, index) => (
                    <Box
                        key={`${item.start || index}-${item.speaker || 'speaker'}`}
                        sx={{
                            p: 1.25,
                            borderRadius: 1,
                            bgcolor: alpha(theme.palette.grey[500], 0.07),
                            border: `1px solid ${alpha(theme.palette.grey[500], 0.08)}`,
                        }}
                    >
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                            {getDisplaySpeakerLabel(item.speaker, recording?.speaker_map)}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.25, whiteSpace: 'pre-wrap' }}>
                            {item.text}
                        </Typography>
                    </Box>
                ))}
            </Stack>

            {hasLongTranscript && (
                <Button
                    size="small"
                    variant="text"
                    onClick={() => setShowFullTranscript((value) => !value)}
                    sx={{ mt: 1 }}
                >
                    {showFullTranscript ? 'Show less' : `Show full transcript (${utterances.length - MAX_VISIBLE_UTTERANCES} more)`}
                </Button>
            )}
        </Box>
    );
}

TranscriptPanel.propTypes = {
    recording: PropTypes.object.isRequired,
    utterances: PropTypes.array.isRequired,
};

function DraftNotePanel({
    canManage,
    draft,
    hasUnknownSpeakers,
    onApprove,
    onDraftChange,
    recording,
}) {
    const noteSaved = Boolean(recording?.lead_note);
    const savedNote = recording?.lead_note?.additional_notes;
    const noteText = noteSaved ? savedNote || draft : draft;
    const shouldShow = recording?.status === 'ready' || Boolean(recording?.generated_note_text) || noteSaved;

    if (!shouldShow) return null;

    return (
        <Box sx={{ mt: 2.5 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2">Draft note</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Review before adding it to lead notes.
                    </Typography>
                </Box>
                {noteSaved && (
                    <Chip
                        size="small"
                        color="success"
                        icon={<Iconify icon="solar:check-circle-bold" width={14} height={14} />}
                        label="Saved"
                    />
                )}
            </Stack>

            {noteSaved && (
                <Alert severity="success" sx={{ mb: 1.5 }}>
                    This generated note has been saved to the lead notes.
                </Alert>
            )}

            <TextField
                fullWidth
                multiline
                minRows={5}
                maxRows={14}
                InputProps={{ readOnly: noteSaved }}
                value={noteText || ''}
                onChange={(event) => onDraftChange(event.target.value)}
                placeholder="Generated note will appear here"
                sx={{
                    ...(noteSaved && {
                        '& .MuiInputBase-root': {
                            bgcolor: 'grey.50',
                        },
                    }),
                }}
            />

            {!noteSaved && hasUnknownSpeakers && (
                <Alert severity="warning" sx={{ mt: 1.5 }}>
                    Speaker roles must be confirmed before saving this note.
                </Alert>
            )}

            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1.5 }}>
                <Button
                    variant="contained"
                    size="small"
                    disabled={!canManage || noteSaved || hasUnknownSpeakers || !draft.trim()}
                    onClick={() => onApprove(recording.id, draft)}
                    startIcon={<Iconify icon={noteSaved ? 'solar:check-circle-bold' : 'solar:diskette-bold'} />}
                >
                    {noteSaved ? 'Saved to notes' : 'Save to lead notes'}
                </Button>
            </Stack>
        </Box>
    );
}

DraftNotePanel.propTypes = {
    canManage: PropTypes.bool.isRequired,
    draft: PropTypes.string.isRequired,
    hasUnknownSpeakers: PropTypes.bool.isRequired,
    onApprove: PropTypes.func.isRequired,
    onDraftChange: PropTypes.func.isRequired,
    recording: PropTypes.object.isRequired,
};

export default function ConsultationRecordingCard({
    recording,
    canManage,
    onRefresh,
    onApprove,
    onSpeakerMapChange,
}) {
    const theme = useTheme();
    const [draft, setDraft] = React.useState(recording?.generated_note_text || '');
    const cfg = statusConfig(recording?.status);
    const utterances = React.useMemo(() => (
        Array.isArray(recording?.transcript_utterances) ? recording.transcript_utterances : []
    ), [recording?.transcript_utterances]);
    const speakerIds = React.useMemo(() => (
        [...new Set(utterances.map((item) => item.speaker).filter(Boolean))]
    ), [utterances]);
    const hasUnknownSpeakers = speakerIds.some(
        (speakerId) => getSpeakerRoleValue(speakerId, recording?.speaker_map) === 'unknown'
    );
    const shouldShowDraft = recording?.status === 'ready'
        || Boolean(recording?.generated_note_text)
        || Boolean(recording?.lead_note);

    React.useEffect(() => {
        setDraft(recording?.generated_note_text || recording?.lead_note?.additional_notes || '');
    }, [recording?.generated_note_text, recording?.lead_note?.additional_notes, recording?.id]);

    if (!recording) return <EmptyRecordingDetail />;

    return (
        <Box
            sx={{
                p: { xs: 1.5, md: 2 },
                borderRadius: 1,
                border: `1px solid ${alpha(theme.palette.grey[500], 0.16)}`,
                bgcolor: 'common.white',
                minWidth: 0,
            }}
        >
            <RecordingHeader recording={recording} cfg={cfg} />

            {recording?.audio_url && (
                <Box sx={{ mt: 1.5 }}>
                    <audio controls src={recording.audio_url} style={{ width: '100%' }} />
                </Box>
            )}

            {recording?.status === 'failed' && (
                <Alert
                    severity="error"
                    sx={{ mt: 1.5 }}
                    action={(
                        <Button
                            color="inherit"
                            size="small"
                            disabled={!canManage}
                            onClick={() => onRefresh(recording.id, true)}
                        >
                            Retry
                        </Button>
                    )}
                >
                    {recording?.error_message || 'Processing failed.'}
                </Alert>
            )}

            <SpeakerAssignmentPanel
                canManage={canManage}
                recording={recording}
                speakerIds={speakerIds}
                hasUnknownSpeakers={hasUnknownSpeakers}
                onSpeakerMapChange={onSpeakerMapChange}
            />

            <TranscriptPanel recording={recording} utterances={utterances} />

            {shouldShowDraft && (
                <>
                    <Divider sx={{ my: 2.5 }} />
                    <DraftNotePanel
                        canManage={canManage}
                        draft={draft}
                        hasUnknownSpeakers={hasUnknownSpeakers}
                        onApprove={onApprove}
                        onDraftChange={setDraft}
                        recording={recording}
                    />
                </>
            )}
        </Box>
    );
}

ConsultationRecordingCard.propTypes = {
    recording: PropTypes.object,
    canManage: PropTypes.bool.isRequired,
    onRefresh: PropTypes.func.isRequired,
    onApprove: PropTypes.func.isRequired,
    onSpeakerMapChange: PropTypes.func.isRequired,
};

ConsultationRecordingCard.defaultProps = {
    recording: null,
};
